import type { QuestionModule } from "../contracts";
import {
  multiSelectCore,
  type MultiSelectDefinition,
  type MultiSelectDraft,
  type MultiSelectEvent,
  type MultiSelectRaw,
  type MultiSelectResponse,
} from "./core";
import { canToggleMultiSelectOption } from "./core";
import {
  MultiSelectButtonsView,
  MultiSelectDropdownView,
  MultiSelectListView,
} from "./views";

export const multiSelectModule: QuestionModule<
  MultiSelectRaw,
  MultiSelectDefinition,
  MultiSelectDraft,
  MultiSelectEvent,
  MultiSelectResponse
> = {
  type: "multi-select",
  galleryFeatures: [
    "visual-groups",
    "baseline",
    "limits",
    "exclusive",
    "custom-text",
    "disabled",
    "images",
    "restored",
  ],
  core: multiSelectCore,
  prompt: (definition) => ({
    content: definition.prompt,
    image: definition.image,
    altText: definition.altText,
    error: definition.error,
  }),
  views: {
    buttons: {
      id: "buttons",
      label: "Buttons",
      component: MultiSelectButtonsView,
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action"],
    },
    list: {
      id: "list",
      label: "List",
      component: MultiSelectListView,
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action"],
    },
    dropdown: {
      id: "dropdown",
      label: "Dropdown",
      component: MultiSelectDropdownView,
      arrangements: ["compact", "wide"],
      requiredControls: ["primary-action"],
      space: "expanded",
    },
  },
  defaultDisplay: { view: "buttons" },
  resolveDisplay: (raw) => ({
    view: raw.style === "button" ? "buttons" : raw.style,
  }),
  controls: (definition, draft, display, copy) => ({
    specify: definition.options.flatMap((option) =>
      option.customPrompt === undefined
        ? []
        : [
            {
              target: { kind: "option-answer" as const, optionId: option.id },
              prompt: option.customPrompt,
              value: draft.customTexts[option.id] ?? "",
              selected: draft.selectedIds.includes(option.id),
              disabled: !canToggleMultiSelectOption(
                definition,
                draft,
                option.id,
              ),
              confirm: (value: string): MultiSelectEvent => ({
                type: "set-custom-answer",
                id: option.id,
                value,
              }),
              remove: { type: "toggle", id: option.id } as MultiSelectEvent,
            },
          ],
    ),
    choiceHint: "multiselect",
    showChoiceHint: display.view !== "dropdown",
    primaryAction: {
      purpose: "submit",
      label: copy.actions.send,
      action: { type: "submit" },
    },
  }),
};
